// ============================================================================
// CreateListingModal.jsx — Sell Your Design / Product
// Supports digital downloads AND physical goods with shipping setup
// ============================================================================

import { useState, useCallback } from 'react'

const API_URL = import.meta.env.VITE_API_URL || ''

const CATEGORIES = [
  { value: 'designs', label: '🎨 Designs', icon: '🎨' },
  { value: 'templates', label: '📐 Templates', icon: '📐' },
  { value: 'fonts', label: '🔤 Fonts', icon: '🔤' },
  { value: 'bundles', label: '📦 Bundles', icon: '📦' },
  { value: 'physical', label: '🏠 Physical Items', icon: '🏠' },
  { value: 'kits', label: '🧵 Craft Kits', icon: '🧵' },
  { value: 'supplies', label: '🛠️ Supplies', icon: '🛠️' }
]

const CRAFT_TYPES = [
  'Cross Stitch', 'Embroidery', 'Sewing', 'Quilting', 'Vinyl', 'Sublimation',
  'Laser Cut', '3D Print', 'Resin', 'Jewelry', 'Book Nook', 'Stained Glass',
  'Knitting', 'Crochet', 'Paper Craft', 'Other'
]

const SHIPPING_TYPES = [
  { value: 'calculated', label: 'Calculated rates (USPS/UPS/FedEx)', desc: 'Real-time rates based on weight and buyer location' },
  { value: 'free', label: 'Free shipping', desc: 'You cover shipping costs (bake into item price)' },
  { value: 'local_pickup', label: 'Local pickup only', desc: 'Buyer picks up in person' }
]

export default function CreateListingModal({ isOpen, onClose, userEmail, onCreated }) {
  const [step, setStep] = useState(1) // 1=basics, 2=pricing, 3=shipping/files, 4=preview
  const [saving, setSaving] = useState(false)
  const [uploadingImage, setUploadingImage] = useState(false)
  const [uploadingFile, setUploadingFile] = useState(false)
  const [error, setError] = useState('')

  const [form, setForm] = useState({
    title: '',
    description: '',
    category: 'designs',
    craft_type: '',
    tags: '',
    item_type: 'digital',
    // Pricing
    price_personal: '',
    price_commercial: '',
    price_extended: '',
    // Images
    thumbnail_url: '',
    preview_urls: [],
    // Digital
    file_url: '',
    file_name: '',
    // Physical
    weight_oz: '',
    dimensions_length: '',
    dimensions_width: '',
    dimensions_height: '',
    shipping_type: 'calculated',
    handling_time_days: 3,
    inventory_count: 1,
    inventory_unlimited: false
  })

  const updateForm = (key, value) => setForm(prev => ({ ...prev, [key]: value }))

  // Image upload
  const handleImageUpload = useCallback(async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 10 * 1024 * 1024) { setError('Image must be under 10MB'); return }

    setUploadingImage(true)
    setError('')
    try {
      const reader = new FileReader()
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1]
        const res = await fetch(`${API_URL}/sellers/upload-listing-image`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-user-email': userEmail },
          body: JSON.stringify({ image_data: base64, filename: file.name })
        })
        const data = await res.json()
        if (data.url) {
          if (!form.thumbnail_url) {
            updateForm('thumbnail_url', data.url)
          }
          updateForm('preview_urls', [...form.preview_urls, data.url])
        }
        setUploadingImage(false)
      }
      reader.readAsDataURL(file)
    } catch (err) {
      setError('Upload failed')
      setUploadingImage(false)
    }
  }, [form.thumbnail_url, form.preview_urls, userEmail])

  // Digital file upload
  const handleFileUpload = useCallback(async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 50 * 1024 * 1024) { setError('File must be under 50MB'); return }

    setUploadingFile(true)
    setError('')
    try {
      const reader = new FileReader()
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1]
        const res = await fetch(`${API_URL}/sellers/upload-digital-file`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-user-email': userEmail },
          body: JSON.stringify({ file_data: base64, filename: file.name })
        })
        const data = await res.json()
        if (data.url) {
          updateForm('file_url', data.url)
          updateForm('file_name', data.filename || file.name)
        }
        setUploadingFile(false)
      }
      reader.readAsDataURL(file)
    } catch (err) {
      setError('Upload failed')
      setUploadingFile(false)
    }
  }, [userEmail])

  const removeImage = (index) => {
    const updated = form.preview_urls.filter((_, i) => i !== index)
    updateForm('preview_urls', updated)
    if (form.thumbnail_url === form.preview_urls[index]) {
      updateForm('thumbnail_url', updated[0] || '')
    }
  }

  // Submit
  const handleSubmit = async () => {
    setSaving(true)
    setError('')
    try {
      const payload = {
        ...form,
        price_personal: parseFloat(form.price_personal),
        price_commercial: form.price_commercial ? parseFloat(form.price_commercial) : null,
        price_extended: form.price_extended ? parseFloat(form.price_extended) : null,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
        weight_oz: form.weight_oz ? parseFloat(form.weight_oz) : null,
        dimensions_length: form.dimensions_length ? parseFloat(form.dimensions_length) : null,
        dimensions_width: form.dimensions_width ? parseFloat(form.dimensions_width) : null,
        dimensions_height: form.dimensions_height ? parseFloat(form.dimensions_height) : null,
        inventory_count: parseInt(form.inventory_count) || 1
      }

      const res = await fetch(`${API_URL}/sellers/listings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-email': userEmail },
        body: JSON.stringify(payload)
      })

      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to create listing')

      onCreated?.(data.listing)
      onClose()
      setForm({ title: '', description: '', category: 'designs', craft_type: '', tags: '', item_type: 'digital', price_personal: '', price_commercial: '', price_extended: '', thumbnail_url: '', preview_urls: [], file_url: '', file_name: '', weight_oz: '', dimensions_length: '', dimensions_width: '', dimensions_height: '', shipping_type: 'calculated', handling_time_days: 3, inventory_count: 1, inventory_unlimited: false })
      setStep(1)
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  const isPhysical = form.item_type === 'physical'

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-2xl max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-zenna-violet to-zenna-cyan px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-white font-display font-bold text-lg">
              {step === 1 ? '📦 What are you selling?' : step === 2 ? '💰 Set your price' : step === 3 ? (isPhysical ? '📦 Shipping details' : '📁 Upload files') : '👀 Preview'}
            </h2>
            <p className="text-white/70 text-xs">Step {step} of 4</p>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white p-1">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-slate-200 dark:bg-slate-700">
          <div className="h-full bg-gradient-to-r from-zenna-violet to-zenna-cyan transition-all duration-300" style={{ width: `${step * 25}%` }} />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-sm rounded-lg">{error}</div>}

          {/* STEP 1: Basics */}
          {step === 1 && (
            <div className="space-y-4">
              {/* Item type toggle */}
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">Item Type</label>
                <div className="flex gap-3">
                  <button onClick={() => updateForm('item_type', 'digital')} className={`flex-1 p-4 rounded-xl border-2 transition-all text-center ${!isPhysical ? 'border-zenna-violet bg-zenna-violet/5' : 'border-slate-200 dark:border-slate-600'}`}>
                    <span className="text-2xl block mb-1">📥</span>
                    <span className="font-medium text-sm text-slate-900 dark:text-white">Digital Download</span>
                    <span className="text-xs text-slate-500 block">Pattern, SVG, font, template</span>
                  </button>
                  <button onClick={() => updateForm('item_type', 'physical')} className={`flex-1 p-4 rounded-xl border-2 transition-all text-center ${isPhysical ? 'border-zenna-violet bg-zenna-violet/5' : 'border-slate-200 dark:border-slate-600'}`}>
                    <span className="text-2xl block mb-1">📦</span>
                    <span className="font-medium text-sm text-slate-900 dark:text-white">Physical Item</span>
                    <span className="text-xs text-slate-500 block">Book nook, kit, handmade</span>
                  </button>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Title *</label>
                <input type="text" value={form.title} onChange={e => updateForm('title', e.target.value)} placeholder="e.g. Cozy Cabin Book Nook Kit" className="w-full mt-1 px-4 py-2.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm" maxLength={120} />
                <span className="text-xs text-slate-400 mt-1 block">{form.title.length}/120</span>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Description *</label>
                <textarea value={form.description} onChange={e => updateForm('description', e.target.value)} rows={4} placeholder="Describe your item in detail. What's included? Materials? Size?" className="w-full mt-1 px-4 py-2.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm resize-none" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Category</label>
                  <select value={form.category} onChange={e => updateForm('category', e.target.value)} className="w-full mt-1 px-4 py-2.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm">
                    {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Craft Type</label>
                  <select value={form.craft_type} onChange={e => updateForm('craft_type', e.target.value)} className="w-full mt-1 px-4 py-2.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm">
                    <option value="">Select craft type</option>
                    {CRAFT_TYPES.map(c => <option key={c} value={c.toLowerCase().replace(/ /g, '_')}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Tags (comma separated)</label>
                <input type="text" value={form.tags} onChange={e => updateForm('tags', e.target.value)} placeholder="book nook, miniature, fairy, diorama" className="w-full mt-1 px-4 py-2.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm" />
              </div>

              {/* Product images */}
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">Product Photos *</label>
                <div className="flex gap-3 flex-wrap">
                  {form.preview_urls.map((url, i) => (
                    <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-600">
                      <img src={url} alt="" className="w-full h-full object-cover" />
                      {form.thumbnail_url === url && <span className="absolute top-0.5 left-0.5 text-[8px] bg-zenna-violet text-white px-1 rounded">Cover</span>}
                      <button onClick={() => removeImage(i)} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center">×</button>
                      {form.thumbnail_url !== url && (
                        <button onClick={() => updateForm('thumbnail_url', url)} className="absolute bottom-0 inset-x-0 bg-black/50 text-white text-[8px] text-center py-0.5">Set cover</button>
                      )}
                    </div>
                  ))}
                  <label className={`w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 flex items-center justify-center cursor-pointer hover:border-zenna-violet transition-colors ${uploadingImage ? 'animate-pulse' : ''}`}>
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploadingImage} />
                    {uploadingImage ? <span className="w-5 h-5 border-2 border-zenna-violet border-t-transparent rounded-full animate-spin" /> : <span className="text-2xl text-slate-400">+</span>}
                  </label>
                </div>
                <p className="text-xs text-slate-400 mt-1">Upload up to 8 photos. First photo is the cover.</p>
              </div>
            </div>
          )}

          {/* STEP 2: Pricing */}
          {step === 2 && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Price (Personal License) *</label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                  <input type="number" step="0.01" min="0" value={form.price_personal} onChange={e => updateForm('price_personal', e.target.value)} placeholder="0.00" className="w-full pl-7 pr-4 py-2.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm" />
                </div>
                <p className="text-xs text-slate-400 mt-1">For personal, non-commercial use</p>
              </div>

              {form.item_type === 'digital' && (
                <>
                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Price (Commercial License)</label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                      <input type="number" step="0.01" min="0" value={form.price_commercial} onChange={e => updateForm('price_commercial', e.target.value)} placeholder="Optional" className="w-full pl-7 pr-4 py-2.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm" />
                    </div>
                    <p className="text-xs text-slate-400 mt-1">For use in products sold commercially</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Price (Extended License)</label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                      <input type="number" step="0.01" min="0" value={form.price_extended} onChange={e => updateForm('price_extended', e.target.value)} placeholder="Optional" className="w-full pl-7 pr-4 py-2.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm" />
                    </div>
                    <p className="text-xs text-slate-400 mt-1">Unlimited commercial use, resale rights</p>
                  </div>
                </>
              )}

              {/* Fee preview */}
              {form.price_personal && (
                <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-4 mt-4">
                  <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">💰 Earnings Preview</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Item price</span>
                      <span className="text-slate-900 dark:text-white">${parseFloat(form.price_personal || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Platform fee (10-15%)</span>
                      <span className="text-red-500">-${(parseFloat(form.price_personal || 0) * 0.15).toFixed(2)} to -${(parseFloat(form.price_personal || 0) * 0.10).toFixed(2)}</span>
                    </div>
                    <div className="border-t border-slate-200 dark:border-slate-600 pt-1 flex justify-between font-medium">
                      <span className="text-slate-700 dark:text-slate-300">You earn</span>
                      <span className="text-zenna-emerald">${(parseFloat(form.price_personal || 0) * 0.85).toFixed(2)} to ${(parseFloat(form.price_personal || 0) * 0.90).toFixed(2)}</span>
                    </div>
                  </div>
                  <p className="text-xs text-slate-400 mt-2">Paid subscribers pay 10% fee, free accounts pay 15%</p>
                </div>
              )}

              {isPhysical && (
                <div>
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Inventory</label>
                  <div className="flex items-center gap-3 mt-1">
                    <input type="number" min="1" value={form.inventory_count} onChange={e => updateForm('inventory_count', e.target.value)} disabled={form.inventory_unlimited} className="w-24 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm" />
                    <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                      <input type="checkbox" checked={form.inventory_unlimited} onChange={e => updateForm('inventory_unlimited', e.target.checked)} className="rounded" />
                      Unlimited / made to order
                    </label>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP 3: Shipping (Physical) or File Upload (Digital) */}
          {step === 3 && (
            <div className="space-y-4">
              {isPhysical ? (
                <>
                  <h3 className="text-sm font-semibold text-slate-800 dark:text-white">Shipping Setup</h3>

                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Shipping Method</label>
                    <div className="space-y-2 mt-2">
                      {SHIPPING_TYPES.map(s => (
                        <label key={s.value} className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${form.shipping_type === s.value ? 'border-zenna-violet bg-zenna-violet/5' : 'border-slate-200 dark:border-slate-600'}`}>
                          <input type="radio" name="shipping" checked={form.shipping_type === s.value} onChange={() => updateForm('shipping_type', s.value)} className="mt-0.5" />
                          <div>
                            <span className="font-medium text-sm text-slate-900 dark:text-white">{s.label}</span>
                            <span className="text-xs text-slate-500 block">{s.desc}</span>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {form.shipping_type === 'calculated' && (
                    <>
                      <div>
                        <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Package Weight (oz) *</label>
                        <input type="number" step="0.1" min="0.1" value={form.weight_oz} onChange={e => updateForm('weight_oz', e.target.value)} placeholder="16" className="w-full mt-1 px-4 py-2.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm" />
                        <p className="text-xs text-slate-400 mt-1">Total weight including packaging. 16 oz = 1 lb</p>
                      </div>

                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Length (in)</label>
                          <input type="number" step="0.5" value={form.dimensions_length} onChange={e => updateForm('dimensions_length', e.target.value)} placeholder="12" className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm" />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Width (in)</label>
                          <input type="number" step="0.5" value={form.dimensions_width} onChange={e => updateForm('dimensions_width', e.target.value)} placeholder="8" className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm" />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Height (in)</label>
                          <input type="number" step="0.5" value={form.dimensions_height} onChange={e => updateForm('dimensions_height', e.target.value)} placeholder="6" className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm" />
                        </div>
                      </div>
                    </>
                  )}

                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Handling Time</label>
                    <select value={form.handling_time_days} onChange={e => updateForm('handling_time_days', parseInt(e.target.value))} className="w-full mt-1 px-4 py-2.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm">
                      <option value={1}>1 business day</option>
                      <option value={3}>1-3 business days</option>
                      <option value={5}>3-5 business days</option>
                      <option value={7}>1 week</option>
                      <option value={14}>2 weeks (made to order)</option>
                    </select>
                  </div>
                </>
              ) : (
                <>
                  <h3 className="text-sm font-semibold text-slate-800 dark:text-white">Upload Your Files</h3>

                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">Digital Product File *</label>
                    {form.file_url ? (
                      <div className="flex items-center gap-3 p-3 bg-zenna-emerald/10 rounded-lg">
                        <svg className="w-5 h-5 text-zenna-emerald flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                        <span className="text-sm text-slate-700 dark:text-slate-300 truncate">{form.file_name}</span>
                        <button onClick={() => { updateForm('file_url', ''); updateForm('file_name', '') }} className="text-xs text-red-500 hover:underline ml-auto">Remove</button>
                      </div>
                    ) : (
                      <label className={`flex flex-col items-center gap-2 p-8 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${uploadingFile ? 'border-zenna-violet animate-pulse' : 'border-slate-300 dark:border-slate-600 hover:border-zenna-violet'}`}>
                        <input type="file" onChange={handleFileUpload} className="hidden" disabled={uploadingFile} />
                        {uploadingFile ? (
                          <span className="w-8 h-8 border-3 border-zenna-violet border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <svg className="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
                        )}
                        <span className="text-sm text-slate-500">Click to upload (SVG, PDF, ZIP, etc. up to 50MB)</span>
                      </label>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* STEP 4: Preview */}
          {step === 4 && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-slate-800 dark:text-white">Preview Your Listing</h3>

              <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl overflow-hidden">
                {form.thumbnail_url && (
                  <img src={form.thumbnail_url} alt="" className="w-full h-48 object-cover" />
                )}
                <div className="p-4">
                  <h4 className="font-bold text-lg text-slate-900 dark:text-white">{form.title || 'Untitled'}</h4>
                  <p className="text-sm text-slate-500 mt-1">{form.description?.substring(0, 150)}{form.description?.length > 150 ? '...' : ''}</p>
                  <div className="flex items-center gap-3 mt-3">
                    <span className="text-xl font-bold text-zenna-violet">${parseFloat(form.price_personal || 0).toFixed(2)}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-300">{isPhysical ? '📦 Physical' : '📥 Digital'}</span>
                    {form.craft_type && <span className="text-xs text-slate-400">{form.craft_type}</span>}
                  </div>
                  {isPhysical && form.shipping_type === 'calculated' && (
                    <p className="text-xs text-slate-400 mt-2">📦 {form.weight_oz}oz • Ships in {form.handling_time_days} day{form.handling_time_days > 1 ? 's' : ''}</p>
                  )}
                  {isPhysical && form.shipping_type === 'free' && (
                    <p className="text-xs text-zenna-emerald mt-2">✅ Free shipping</p>
                  )}
                  {!isPhysical && (
                    <p className="text-xs text-zenna-cyan mt-2">📥 Instant digital download</p>
                  )}
                </div>
              </div>

              {form.preview_urls.length > 1 && (
                <div className="flex gap-2 overflow-x-auto">
                  {form.preview_urls.map((url, i) => (
                    <img key={i} src={url} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 dark:border-slate-700 px-6 py-4 flex items-center justify-between flex-shrink-0">
          {step > 1 ? (
            <button onClick={() => setStep(step - 1)} className="text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400">← Back</button>
          ) : (
            <button onClick={onClose} className="text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400">Cancel</button>
          )}

          {step < 4 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={
                (step === 1 && (!form.title || !form.description || form.preview_urls.length === 0)) ||
                (step === 2 && !form.price_personal) ||
                (step === 3 && !isPhysical && !form.file_url) ||
                (step === 3 && isPhysical && form.shipping_type === 'calculated' && !form.weight_oz)
              }
              className="px-6 py-2 bg-gradient-zenna text-white rounded-lg text-sm font-medium disabled:opacity-50 hover:shadow-lg transition-all"
            >
              Next →
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="px-6 py-2 bg-gradient-zenna text-white rounded-lg text-sm font-medium disabled:opacity-50 hover:shadow-lg transition-all"
            >
              {saving ? 'Publishing...' : '🚀 Publish Listing'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}